import {
  type CustomBlockingRulesOption,
  CustomBlockingRulesOptions,
  CustomBlockingRulesSubdomain,
  type PublisherInventoryRule,
} from '@openx/types/targeting/inventoryAndContent';
import { type JSX, useCallback } from 'react';
import type { AnyAction } from 'redux';
import { setPublisherInventoryRules } from '../../shared/InventoryAndContent/inventoryHelpers';

export type UseCustomBlockingRulesOperations = {
  dispatchInventory: (action: AnyAction) => void;
  selectedItems: PublisherInventoryRule[];
};

export const useCustomBlockingRulesOperations = ({
  dispatchInventory,
  selectedItems,
}: UseCustomBlockingRulesOperations) => {
  const onRemoveAll = (type: CustomBlockingRulesOption) => {
    const updatedItems = selectedItems.filter(item => item.blockingRuleOption !== type);

    dispatchInventory({
      payload: updatedItems,
      type: setPublisherInventoryRules,
    } as unknown as AnyAction);
  };

  const onRemoveSelectedRule = (selectedRule: PublisherInventoryRule) => {
    const updatedItems = selectedItems.filter(item => {
      if (item.publisher_id !== selectedRule.publisher_id) return item;

      return item.blockingRuleOption !== selectedRule.blockingRuleOption;
    });

    dispatchInventory({
      payload: updatedItems,
      type: setPublisherInventoryRules,
    } as unknown as AnyAction);
  };

  const onRemoveSingleEntry = useCallback(
    (item: string | JSX.Element, customRule: PublisherInventoryRule) => {
      const ruletoUpdateIndex = selectedItems.findIndex(
        rule =>
          rule.publisher_id === customRule.publisher_id && rule.blockingRuleOption === customRule.blockingRuleOption
      );

      const ruleToUpdate = selectedItems[ruletoUpdateIndex];
      const updatedRules = [...selectedItems];
      const isDomainRule = !!customRule.domain.length;
      const ruleTargetingDomainType = isDomainRule ? CustomBlockingRulesOptions.DOMAIN : CustomBlockingRulesSubdomain;

      const ruleTargetingKey =
        customRule.blockingRuleOption === CustomBlockingRulesOptions.BUNDLE_ID
          ? CustomBlockingRulesOptions.BUNDLE_ID
          : ruleTargetingDomainType;

      const filteredRuleData = ruleToUpdate[ruleTargetingKey].filter(
        (updatedRuleItem: string) => updatedRuleItem !== item
      );

      if (filteredRuleData.length) {
        updatedRules[ruletoUpdateIndex] = {
          ...ruleToUpdate,
          [ruleTargetingKey]: filteredRuleData,
        };
      } else {
        updatedRules.splice(ruletoUpdateIndex, 1);
      }

      dispatchInventory({
        payload: updatedRules,
        type: setPublisherInventoryRules,
      } as unknown as AnyAction);
    },
    [dispatchInventory, selectedItems]
  );

  return { onRemoveAll, onRemoveSelectedRule, onRemoveSingleEntry };
};
